import React from 'react';
import clsx from 'clsx';

/**
 * Пропсы для компонента SubmitButton
 */
interface SubmitButtonProps {
  isLoading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  loadingText?: string;
  className?: string;
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  icon?: string;
  type?: 'submit' | 'button';
  onClick?: () => void;
}

/**
 * Компонент кнопки отправки формы с состоянием загрузки
 * Автоматически показывает спиннер и отключается во время загрузки
 * Следует принципам DaisyUI и обеспечивает доступность
 *
 * @param isLoading - Состояние загрузки
 * @param disabled - Отключена ли кнопка
 * @param children - Содержимое кнопки
 * @param loadingText - Текст во время загрузки
 * @param className - Дополнительные CSS классы
 * @param variant - Вариант стиля кнопки
 * @param size - Размер кнопки
 * @param fullWidth - Растянуть на всю ширину
 * @param icon - CSS класс иконки FontAwesome
 * @param type - Тип кнопки
 * @param onClick - Обработчик клика
 * @returns JSX элемент
 */
export const SubmitButton: React.FC<SubmitButtonProps> = ({
  isLoading = false,
  disabled = false,
  children,
  loadingText = 'Загрузка...',
  className,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  type = 'submit',
  onClick
}) => {
  const buttonClasses = clsx(
    'btn',
    {
      // Варианты стилей
      'btn-primary': variant === 'primary',
      'btn-secondary': variant === 'secondary',
      'btn-success': variant === 'success',
      'btn-warning': variant === 'warning',
      'btn-error': variant === 'error',
      'btn-info': variant === 'info',

      // Размеры
      'btn-xs': size === 'xs',
      'btn-sm': size === 'sm',
      'btn-md': size === 'md',
      'btn-lg': size === 'lg',

      // Полная ширина
      'w-full': fullWidth
    },
    className
  );

  const isDisabled = disabled || isLoading;

  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={isDisabled}
      onClick={onClick}
      aria-busy={isLoading}
      aria-disabled={isDisabled}
    >
      {isLoading ? (
        <>
          <span
            className="loading loading-spinner loading-sm"
            aria-hidden="true"
          ></span>
          <span>{loadingText}</span>
        </>
      ) : (
        <>
          {icon && <i className={`${icon} text-sm`} aria-hidden="true"></i>}
          <span>{children}</span>
        </>
      )}
    </button>
  );
};
